import { useState, useEffect } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import { describeCustomerSaveFailure } from "@crm-src/lib/customer-save-failure"
import { useCan } from "@/auth/permissions"

import { MultiUserSelector } from "@crm-src/components/multi-user-selector"
import { UserSelector } from "@crm-src/components/user-selector"
import { ResellerSelector } from "@crm-src/components/reseller-selector"
import { ResellerOnly } from "@crm-src/components/reseller"
import { ProductSelector } from "@crm-src/components/product-selector"
import { AddContactsAfterCreationModal } from "@crm-src/components/add-contacts-after-creation-modal"
import { customersApi } from "@crm-src/lib/customers-api"
import { leadsApi } from "@crm-src/lib/leads-api"
import type {
  DetailedCustomer,
  CustomerUpdateParams,
  CustomerCreateParams,
  CustomerBusinessCategory
} from "@crm-src/types/customer"
import type { CreateLeadData } from "@crm-src/types/lead"

interface CustomerFormData {
  // Company Information
  company_name?: string
  website?: string
  business_category?: CustomerBusinessCategory
  prospect_source?: string

  // Product Information
  product_id?: string
  product_feature_id?: string

  // Reseller Information
  reseller_id?: string | null

  // Assignment Information
  assigned_to_id?: string
  assigned_salespeople_ids?: string[]

  // Address Information
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  timezone?: string
}

interface CustomerFormModalProps {
  isOpen: boolean
  onClose: () => void
  customer?: DetailedCustomer | null  // If null/undefined = create mode, if provided = edit mode
  onSuccess: (customer: DetailedCustomer) => void
  mode?: 'customer' | 'lead'
  initialData?: Partial<CustomerFormData>
}

export function CustomerFormModal({
  isOpen,
  onClose,
  customer,
  onSuccess,
  mode = 'customer',
  initialData
}: CustomerFormModalProps) {
  const { toast } = useToast()
  // WHO MANAGES A RECORD (CEO, 2026-09-16): "a salesperson should be capable
  // to do everything with an assigned customer". Management follows visibility
  // — every row on this page is one this user can see (crm-api narrows the list)
  // — so the controls follow `crm:edit`, not "is an administrator". crm-api
  // enforces the same rule (CustomerManagement); a read-only role such as
  // marketing_manager still gets no write controls.
  const canManage = useCan("crm:edit")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!customer
  // Product / Feature / Version / Reseller live in the Agreement section
  // for existing customers (see edit-agreement-modal.tsx). Keep them in
  // the modal for lead mode (no Agreement exists yet) and for customer
  // create mode (so the initial customer record can be seeded with them).
  const showProductResellerSection = mode === 'lead' || !isEditMode

  // State for showing add contacts modal after creation
  const [showAddContactsModal, setShowAddContactsModal] = useState(false)
  const [createdCustomer, setCreatedCustomer] = useState<DetailedCustomer | null>(null)

  // Form data
  const [formData, setFormData] = useState<CustomerFormData>({
    company_name: "",
    website: "",
    business_category: "equipment_purchaser",
    prospect_source: "",
    product_id: undefined,
    product_feature_id: undefined,
    reseller_id: null,
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    timezone: "",
    assigned_salespeople_ids: [],
    assigned_to_id: undefined,
  })

  // Populate form data when customer changes or modal opens
  useEffect(() => {
    if (isOpen) {
      if (customer) {
        // Edit mode - populate with existing customer data
        setFormData({
          company_name: customer.company_name || "",
          website: customer.website || "",
          business_category: customer.business_category || "equipment_purchaser",
          prospect_source: (customer as any).prospect_source || "",
          product_id: customer.product_id?.toString() || undefined,
          product_feature_id: customer.product_feature_id?.toString() || undefined,
          reseller_id: customer.reseller_id?.toString() || null,
          address: customer.address || "",
          city: customer.city || "",
          state: customer.state || "",
          country: customer.country || "",
          postal_code: customer.postal_code || "",
          timezone: customer.timezone || "",
          assigned_salespeople_ids: customer.assigned_salespeople?.map(sp => sp.user_id) || [],
          assigned_to_id: customer.assigned_to_id?.toString() || undefined,
        })
      } else {
        // Create mode - reset to empty form or initial data
        setFormData({
          company_name: initialData?.company_name || "",
          website: initialData?.website || "",
          business_category: initialData?.business_category || "equipment_purchaser",
          prospect_source: initialData?.prospect_source || "",
          product_id: initialData?.product_id || undefined,
          product_feature_id: initialData?.product_feature_id || undefined,
          reseller_id: initialData?.reseller_id || null,
          address: initialData?.address || "",
          city: initialData?.city || "",
          state: initialData?.state || "",
          country: initialData?.country || "",
          postal_code: initialData?.postal_code || "",
          timezone: initialData?.timezone || "",
          assigned_salespeople_ids: initialData?.assigned_salespeople_ids || [],
          assigned_to_id: initialData?.assigned_to_id || undefined,
        })
      }
    }
  }, [customer, isOpen, initialData])

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)

      if (isEditMode && customer) {
        // Update existing customer. Product / feature / version / reseller
        // are owned by the Agreement section for customers; only include
        // them when editing a lead (no Agreement exists yet).
        const updateData: CustomerUpdateParams = {
          company_name: formData.company_name,
          website: formData.website,
          business_category: formData.business_category,
          ...(showProductResellerSection ? {
            product_id: formData.product_id && formData.product_id !== 'none' ? parseInt(formData.product_id) : undefined,
            product_feature_id: formData.product_feature_id && formData.product_feature_id !== 'none' ? parseInt(formData.product_feature_id) : undefined,
            reseller_id: formData.reseller_id && formData.reseller_id !== 'none' ? parseInt(formData.reseller_id) : undefined,
          } : {}),
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          postal_code: formData.postal_code,
          timezone: formData.timezone,
          assigned_salespeople_ids: formData.assigned_salespeople_ids,
        }

        // Call appropriate API based on mode
        let response;
        if (mode === 'lead') {
           // For leads, we might need to cast to UpdateLeadData
           const leadUpdateData: any = { ...updateData, prospect_source: formData.prospect_source };
           response = await leadsApi.update(customer.id, leadUpdateData);
           // Response format might differ slightly
           response = { customer: response.lead }; // Normalize to expected format
        } else {
           response = await customersApi.update(customer.id, updateData);
        }

        toast({
          title: "Success",
          description: `${mode === 'lead' ? 'Prospect' : 'Customer'} updated successfully`,
        })

        onSuccess(response.customer as DetailedCustomer)
      } else {
        // Create new customer/lead
        const baseData = {
          first_name: "", // Required field for creation
          last_name: "",  // Required field for creation
          email: "",      // Required field for creation
          company_name: formData.company_name || "",
          website: formData.website,
          product_id: formData.product_id && formData.product_id !== 'none' ? formData.product_id : undefined,
          product_feature_id: formData.product_feature_id && formData.product_feature_id !== 'none' ? formData.product_feature_id : undefined,
          reseller_id: formData.reseller_id && formData.reseller_id !== 'none' ? formData.reseller_id : undefined,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          postal_code: formData.postal_code,
          timezone: formData.timezone,
        };

        // Nobody picked: crm-api attaches the creator as the salesperson.
        const assignmentData: any = {};
        if (canManage && formData.assigned_to_id) {
          assignmentData.assigned_to_id = formData.assigned_to_id;
        }

        let newRecord: DetailedCustomer;

        if (mode === 'lead') {
          const createLeadData: CreateLeadData = {
            ...baseData,
            ...assignmentData,
            business_category: formData.business_category,
            prospect_source: formData.prospect_source,
            priority: 'medium', // Default priority
            prospected_value: 0,
            prospected_ports: 0,
          };
          const response = await leadsApi.create(createLeadData);
          newRecord = response.lead as unknown as DetailedCustomer;
        } else {
          const createCustomerData: CustomerCreateParams = {
            ...baseData,
            ...assignmentData,
            business_category: formData.business_category,
            product_id: baseData.product_id ? parseInt(baseData.product_id) : undefined,
            product_feature_id: baseData.product_feature_id ? parseInt(baseData.product_feature_id) : undefined,
            // Ensure required fields for CustomerCreateParams
            status: "active",
            lifecycle_stage: "new_customer",
            customer_type: "regular",
            account_tier: "standard",
            support_level: "none",
            onboarding_status: "not_started",
            risk_level: "low",
          };
          const response = await customersApi.create(createCustomerData);
          newRecord = response.customer as DetailedCustomer;
        }

        // Don't show toast or close modal yet - show add contacts modal instead
        setCreatedCustomer(newRecord)
        setShowAddContactsModal(true)

        // Call onSuccess to update parent list
        onSuccess(newRecord)
      }

      onClose()
    } catch (error) {
      console.error(`Error submitting ${mode}:`, error)
      // SAY WHAT THE SERVER SAID (2026-09-16). This printed one fixed sentence
      // and dropped the response, so a salesperson refused for a duplicate
      // company — a name already on another rep's books, invisible to him
      // under own-records scope — read "Failed to create customer" and had
      // nothing to act on.
      const failure = describeCustomerSaveFailure(error, {
        mode,
        isEditMode,
        ...(formData.company_name ? { companyName: formData.company_name } : {}),
      })
      toast({
        title: failure.title,
        description: failure.description,
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  // Handle when add contacts modal closes
  const handleAddContactsClose = () => {
    setShowAddContactsModal(false)
    setCreatedCustomer(null)
    onClose() // Close the main customer form modal
  }

  return (
    <>
      <Dialog
        open={isOpen && !showAddContactsModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { minWidth: 700, maxWidth: "90vw" } }}
      >
        <DialogTitle>{isEditMode ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isEditMode
              ? 'Update customer business information and assignments.'
              : 'Create a new customer record.'}
          </DialogContentText>

          <Box
            component="form"
            id="customer-form"
            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            autoComplete="on"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Company Information */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  id="company_name"
                  name="company_name"
                  label="Company Name"
                  size="small"
                  fullWidth
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Company Name"
                  autoComplete="organization"
                />

                <TextField
                  id="business_category"
                  select
                  label="Business Type"
                  size="small"
                  fullWidth
                  value={formData.business_category}
                  onChange={(e) => setFormData({ ...formData, business_category: e.target.value as CustomerBusinessCategory })}
                >
                  <MenuItem value="service">Service Provider</MenuItem>
                  <MenuItem value="equipment_purchaser">Equipment Purchaser</MenuItem>
                </TextField>

                <TextField
                  id="website"
                  name="website"
                  label="Website"
                  size="small"
                  fullWidth
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://company.com"
                  autoComplete="url"
                />
              </Box>

              {/* Product and Reseller Information — only shown when this
                  customer doesn't yet have an Agreement (lead mode, or
                  creating a new customer). For existing customers, these
                  fields live in the Agreement section on the detail page. */}
              {showProductResellerSection && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }} />

                  {/* Product Selection */}
                  <Box>
                    <ProductSelector
                      productId={formData.product_id}
                      productFeatureId={formData.product_feature_id}
                      onProductChange={(productId) => setFormData(prev => ({ ...prev, product_id: productId }))}
                      onProductFeatureChange={(featureId) => setFormData(prev => ({ ...prev, product_feature_id: featureId }))}
                    />
                  </Box>

                  {/* Reseller Selection — reseller wall: hidden for a viewer
                      without crm:resellers. See components/reseller.tsx. */}
                  <ResellerOnly>
                    <Box>
                      <ResellerSelector
                        value={formData.reseller_id}
                        onValueChange={(value) => setFormData({ ...formData, reseller_id: value })}
                        placeholder="Select a reseller..."
                      />
                    </Box>
                  </ResellerOnly>
                </Box>
              )}

              {/* Assignment — anyone who may edit the record may staff it (CustomerManagement). */}
              {canManage && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
                  <Box>
                    {isEditMode ? (
                      <MultiUserSelector
                        value={formData.assigned_salespeople_ids || []}
                        onValueChange={(value) => setFormData({ ...formData, assigned_salespeople_ids: value })}
                        label="Assigned Salespeople"
                        placeholder="Select salespeople..."
                      />
                    ) : (
                      <UserSelector
                        value={formData.assigned_to_id}
                        onValueChange={(value) => setFormData({ ...formData, assigned_to_id: value })}
                        label="Assigned Salesperson"
                        placeholder="Select a salesperson..."
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Address Information */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Street Address - Full Width */}
                  <TextField
                    id="address"
                    name="address"
                    label="Street Address"
                    size="small"
                    fullWidth
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street"
                    autoComplete="street-address"
                  />

                  {/* City and State - First Row */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      id="city"
                      name="city"
                      label="City"
                      size="small"
                      fullWidth
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="New York"
                      autoComplete="address-level2"
                    />
                    <TextField
                      id="state"
                      name="state"
                      label="State/Province"
                      size="small"
                      fullWidth
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="NY"
                      autoComplete="address-level1"
                    />
                  </Box>

                  {/* Country and Postal - Second Row */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      id="country"
                      name="country"
                      label="Country"
                      size="small"
                      fullWidth
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="United States"
                      autoComplete="country-name"
                    />
                    <TextField
                      id="postal_code"
                      name="postal_code"
                      label="Postal Code"
                      size="small"
                      fullWidth
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="10001"
                      autoComplete="postal-code"
                    />
                  </Box>

                  {/* Timezone */}
                  <TextField
                    id="timezone"
                    name="timezone"
                    label="Timezone"
                    size="small"
                    fullWidth
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    placeholder="America/New_York"
                    autoComplete="off"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isSubmitting} type="button">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            type="button"
            {...(isSubmitting ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
          >
            {isSubmitting
              ? (isEditMode ? 'Updating...' : 'Creating...')
              : (isEditMode ? 'Update Customer' : 'Add Customer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contacts After Creation Modal */}
      {createdCustomer && (
        <AddContactsAfterCreationModal
          isOpen={showAddContactsModal}
          onClose={handleAddContactsClose}
          entityId={createdCustomer.id}
          entityName={createdCustomer.company_name || createdCustomer.full_name || "Customer"}
          entityType="customer"
        />
      )}
    </>
  )
}
